/**
 * NHL Season Detection Utility
 *
 * Automatically determines the current NHL season stage based on the current date.
 * This eliminates the need to manually update default season values across the app.
 *
 * NHL Season Structure:
 * - Regular Season: 82 games per team (October through April)
 * - Playoffs: 4 rounds - First Round, Conference Semifinals, Conference Finals, Stanley Cup Finals
 * - Off-season: July through September
 *
 * Key Dates (typical):
 * - Season starts: Early October
 * - Regular season ends: Mid-April
 * - Playoffs: Mid-April through June
 * - Stanley Cup Finals: June
 */

/**
 * NHL 2024-25 Season Schedule
 * Regular season starts: October 8, 2024
 * Regular season ends: April 17, 2025
 */
const NHL_2024_25_SEASON = {
  start: new Date('2024-10-08T00:00:00'),
  regularSeasonEnd: new Date('2025-04-17T23:59:59'),
  playoffs: {
    firstRound: { start: new Date('2025-04-20T00:00:00'), end: new Date('2025-05-04T23:59:59') },
    secondRound: { start: new Date('2025-05-05T00:00:00'), end: new Date('2025-05-22T23:59:59') },
    conferenceFinals: { start: new Date('2025-05-23T00:00:00'), end: new Date('2025-06-08T23:59:59') },
    stanleyCup: { start: new Date('2025-06-09T00:00:00'), end: new Date('2025-06-30T23:59:59') }
  }
};

/**
 * NHL 2025-26 Season Schedule (estimated)
 * Regular season starts: October 7, 2025 (estimated)
 * Regular season ends: April 16, 2026 (estimated)
 */
const NHL_2025_26_SEASON = {
  start: new Date('2025-10-07T00:00:00'),
  regularSeasonEnd: new Date('2026-04-16T23:59:59'),
  playoffs: {
    firstRound: { start: new Date('2026-04-19T00:00:00'), end: new Date('2026-05-03T23:59:59') },
    secondRound: { start: new Date('2026-05-04T00:00:00'), end: new Date('2026-05-21T23:59:59') },
    conferenceFinals: { start: new Date('2026-05-22T00:00:00'), end: new Date('2026-06-07T23:59:59') },
    stanleyCup: { start: new Date('2026-06-08T00:00:00'), end: new Date('2026-06-29T23:59:59') }
  }
};

/**
 * Get season data for a specific year
 * @param {number} startYear - The year the season starts (e.g., 2024 for 2024-25 season)
 * @returns {Object} Season data
 */
const getSeasonData = (startYear) => {
  if (startYear === 2024) return NHL_2024_25_SEASON;
  if (startYear === 2025) return NHL_2025_26_SEASON;

  // For future years, estimate based on typical NHL schedule
  // NHL typically starts first week of October
  const seasonStart = new Date(startYear, 9, 8); // October 8
  const regularSeasonEnd = new Date(startYear + 1, 3, 17); // April 17 next year

  return {
    start: seasonStart,
    regularSeasonEnd: regularSeasonEnd,
    playoffs: {
      firstRound: {
        start: new Date(startYear + 1, 3, 20), // April 20
        end: new Date(startYear + 1, 4, 4)     // May 4
      },
      secondRound: {
        start: new Date(startYear + 1, 4, 5),  // May 5
        end: new Date(startYear + 1, 4, 22)    // May 22
      },
      conferenceFinals: {
        start: new Date(startYear + 1, 4, 23), // May 23
        end: new Date(startYear + 1, 5, 8)     // June 8
      },
      stanleyCup: {
        start: new Date(startYear + 1, 5, 9),  // June 9
        end: new Date(startYear + 1, 5, 30)    // June 30
      }
    }
  };
};

/**
 * Determines the current NHL season based on today's date
 *
 * Logic:
 * 1. NHL seasons run from October (year N) through June (year N+1)
 * 2. Season is identified as "YYYY-YY" (e.g., "2024-25")
 * 3. Returns season stage (regular, playoffs, offseason)
 * 4. Returns specific playoff round if in playoffs
 *
 * @param {Date} [currentDate=new Date()] - The date to check (defaults to now)
 * @returns {Object} Object containing season, stage, round, and display name
 */
export const getCurrentNHLSeason = (currentDate = new Date()) => {
  const now = new Date(currentDate);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  // Determine which season we're in
  // NHL season runs October-June
  // If it's Jan-Sep, we're in the season that started LAST year
  // If it's Oct-Dec, we're in the season that starts THIS year
  let startYear;
  if (currentMonth >= 10) {
    startYear = currentYear;
  } else {
    startYear = currentYear - 1;
  }

  const seasonData = getSeasonData(startYear);
  const seasonId = `${startYear}-${String(startYear + 1).slice(-2)}`;

  // Check if before season starts
  if (now < seasonData.start) {
    // Off-season before current season
    if (currentMonth >= 7) {
      // Late off-season (Jul-Sep) - show upcoming season
      return {
        season: seasonId,
        stage: 'offseason',
        round: null,
        name: `${seasonId} Off-season`,
        displayName: 'Off-season'
      };
    } else {
      // Early off-season (after previous season ended)
      const prevYear = startYear - 1;
      return {
        season: `${prevYear}-${String(startYear).slice(-2)}`,
        stage: 'offseason',
        round: null,
        name: `${prevYear}-${String(startYear).slice(-2)} Off-season`,
        displayName: 'Off-season'
      };
    }
  }

  // Check if in regular season
  if (now >= seasonData.start && now <= seasonData.regularSeasonEnd) {
    return {
      season: seasonId,
      stage: 'regular',
      round: null,
      name: `${seasonId} Regular Season`,
      displayName: 'Regular Season'
    };
  }

  // Check playoff rounds
  for (const [roundKey, roundData] of Object.entries(seasonData.playoffs)) {
    if (now >= roundData.start && now <= roundData.end) {
      const roundNames = {
        firstRound: 'First Round',
        secondRound: 'Conference Semifinals',
        conferenceFinals: 'Conference Finals',
        stanleyCup: 'Stanley Cup Finals'
      };

      return {
        season: seasonId,
        stage: 'playoffs',
        round: roundKey,
        name: roundNames[roundKey],
        displayName: roundNames[roundKey]
      };
    }
  }

  // After Stanley Cup Finals - off-season
  return {
    season: seasonId,
    stage: 'offseason',
    round: null,
    name: `${seasonId} Off-season`,
    displayName: 'Off-season'
  };
};

/**
 * Get the current NHL season year identifier
 * @param {Date} [currentDate=new Date()] - The date to check
 * @returns {string} The season identifier (e.g., '2024-25')
 */
export const getCurrentSeasonYear = (currentDate = new Date()) => {
  const seasonInfo = getCurrentNHLSeason(currentDate);
  return seasonInfo.season;
};

/**
 * Format season info for display
 * @param {Object} seasonInfo - Season info from getCurrentNHLSeason()
 * @returns {string} Formatted season label
 */
export const formatNHLSeason = (seasonInfo) => {
  if (!seasonInfo) return 'Regular Season';
  return seasonInfo.displayName || seasonInfo.name || 'Regular Season';
};

/**
 * Check if we're currently in the NHL regular season
 * @param {Date} [currentDate=new Date()] - The date to check
 * @returns {boolean} True if in regular season
 */
export const isNHLRegularSeason = (currentDate = new Date()) => {
  const seasonInfo = getCurrentNHLSeason(currentDate);
  return seasonInfo.stage === 'regular';
};

/**
 * Check if we're currently in the NHL playoffs
 * @param {Date} [currentDate=new Date()] - The date to check
 * @returns {boolean} True if in playoffs
 */
export const isNHLPlayoffs = (currentDate = new Date()) => {
  const seasonInfo = getCurrentNHLSeason(currentDate);
  return seasonInfo.stage === 'playoffs';
};

/**
 * Check if we're in the off-season
 * @param {Date} [currentDate=new Date()] - The date to check
 * @returns {boolean} True if in off-season
 */
export const isNHLOffseason = (currentDate = new Date()) => {
  const seasonInfo = getCurrentNHLSeason(currentDate);
  return seasonInfo.stage === 'offseason';
};

/**
 * Get date range for current season stage
 * @param {Date} [currentDate=new Date()] - The date to check
 * @returns {Object} Start and end dates for current stage
 */
export const getCurrentSeasonDateRange = (currentDate = new Date()) => {
  const seasonInfo = getCurrentNHLSeason(currentDate);
  const now = new Date(currentDate);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  let startYear = currentMonth >= 10 ? currentYear : currentYear - 1;
  const seasonData = getSeasonData(startYear);

  if (seasonInfo.stage === 'regular') {
    return {
      start: seasonData.start,
      end: seasonData.regularSeasonEnd
    };
  }

  if (seasonInfo.stage === 'playoffs' && seasonInfo.round) {
    return seasonData.playoffs[seasonInfo.round];
  }

  return {
    start: null,
    end: null
  };
};

export default {
  getCurrentNHLSeason,
  getCurrentSeasonYear,
  formatNHLSeason,
  isNHLRegularSeason,
  isNHLPlayoffs,
  isNHLOffseason,
  getCurrentSeasonDateRange
};
